import {
  SurchargeConditionChildType,
  SurchargeConditionOperator,
  SurchargeConditionOperatorMap,
  SurchargeConditionProperty,
  SurchargeConditionPropertyType,
  SurchargeConditionPropertyTypeMap,
} from '@invenco/shipping-interface';
import type {
  SurchargeConditionDTO,
  SurchargePropertyConditionDTO,
} from '@invenco/shipping-interface';

/** Human labels for the surcharge condition properties shown in the builder and the read views. */
export const surchargeConditionPropertyTitle: Record<SurchargeConditionProperty, string> = {
  [SurchargeConditionProperty.CARRIER_CODE]: 'Carrier code',
  [SurchargeConditionProperty.CARRIER_SERVICE_CODE]: 'Carrier service code',
  [SurchargeConditionProperty.DESTINATION_COUNTRY_CODE]: 'Destination country code',
  [SurchargeConditionProperty.VOLUMETRIC_WEIGHT_KG]: 'Volumetric weight (kg)',
};

/** Human labels for the operators, reused by the builder select and the readable rendering. */
export const surchargeConditionOperatorTitle: Record<SurchargeConditionOperator, string> = {
  [SurchargeConditionOperator.EQUAL_TO]: 'is equal to',
  [SurchargeConditionOperator.NOT_EQUAL_TO]: 'is not equal to',
  [SurchargeConditionOperator.IN]: 'is one of',
  [SurchargeConditionOperator.NOT_IN]: 'is not one of',
  [SurchargeConditionOperator.GREATER_THAN]: 'is greater than',
  [SurchargeConditionOperator.GREATER_THAN_OR_EQUAL_TO]: 'is greater than or equal to',
  [SurchargeConditionOperator.LESS_THAN]: 'is less than',
  [SurchargeConditionOperator.LESS_THAN_OR_EQUAL_TO]: 'is less than or equal to',
  [SurchargeConditionOperator.STARTS_WITH]: 'starts with',
  [SurchargeConditionOperator.ENDS_WITH]: 'ends with',
  [SurchargeConditionOperator.STRING_CONTAINS]: 'contains',
};

export const surchargeConditionChildTypeTitle: Record<SurchargeConditionChildType, string> = {
  [SurchargeConditionChildType.ALL]: 'All',
  [SurchargeConditionChildType.ANY]: 'Any',
};

/** The two operators whose value is an array (rendered as chips, not a single field). */
export const ARRAY_OPERATORS: readonly SurchargeConditionOperator[] = [
  SurchargeConditionOperator.IN,
  SurchargeConditionOperator.NOT_IN,
];

export const isArrayOperator = (operator: SurchargeConditionOperator): boolean =>
  ARRAY_OPERATORS.includes(operator);

export const propertyType = (
  property: SurchargeConditionProperty,
): SurchargeConditionPropertyType => SurchargeConditionPropertyTypeMap[property];

/** Operators valid for a property, derived from its value type (string vs number). */
export const operatorsForProperty = (
  property: SurchargeConditionProperty,
): SurchargeConditionOperator[] => SurchargeConditionOperatorMap[propertyType(property)];

/**
 * A single condition row as held in the form. `value` is a scalar for scalar operators and a
 * string[] for IN/NOT_IN; numbers are kept as strings while editing and parsed on submit.
 */
export type SurchargeConditionRow = {
  property: SurchargeConditionProperty;
  operator: SurchargeConditionOperator;
  value: string;
  /** Chip values for the array operators (IN/NOT_IN). */
  values: string[];
};

/** The applicability section of the form: the All/Any grouping plus its (single-level) rows. */
export type SurchargeConditionsForm = {
  enabled: boolean;
  childConditionType: SurchargeConditionChildType;
  rows: SurchargeConditionRow[];
};

export const emptyConditionRow = (): SurchargeConditionRow => ({
  property: SurchargeConditionProperty.CARRIER_CODE,
  operator: SurchargeConditionOperator.EQUAL_TO,
  value: '',
  values: [],
});

export const emptyConditionsForm = (): SurchargeConditionsForm => ({
  enabled: false,
  childConditionType: SurchargeConditionChildType.ALL,
  rows: [emptyConditionRow()],
});

/** True once a row carries a value appropriate for its operator (used by validation + build). */
export function isRowComplete(row: SurchargeConditionRow): boolean {
  if (isArrayOperator(row.operator)) return row.values.length > 0;
  if (propertyType(row.property) === SurchargeConditionPropertyType.NUMBER) {
    return row.value.trim() !== '' && Number.isFinite(Number(row.value));
  }
  return row.value.trim() !== '';
}

/** Builds the property-condition value for the DTO, coercing number properties and array chips. */
function buildRowValue(row: SurchargeConditionRow): SurchargePropertyConditionDTO['value'] {
  const isNumber = propertyType(row.property) === SurchargeConditionPropertyType.NUMBER;
  if (isArrayOperator(row.operator)) {
    return isNumber ? row.values.map(Number) : row.values;
  }
  return isNumber ? Number(row.value) : row.value;
}

/**
 * Converts the form's applicability section to a `SurchargeConditionDTO`, or `null` when conditions
 * are disabled or no row is complete (so the surcharge omits `applicability` entirely).
 */
export function buildApplicability(form: SurchargeConditionsForm): SurchargeConditionDTO | null {
  if (!form.enabled) return null;
  const completeRows = form.rows.filter(isRowComplete);
  if (completeRows.length === 0) return null;
  return {
    childConditionType: form.childConditionType,
    childConditions: completeRows.map((row) => ({
      property: row.property,
      operator: row.operator,
      value: buildRowValue(row),
    })),
  };
}

const isPropertyCondition = (
  child: SurchargePropertyConditionDTO | SurchargeConditionDTO,
): child is SurchargePropertyConditionDTO => 'property' in child;

/** Renders one property condition as "Carrier code is equal to TGE" / "… is one of [NZ, AU]". */
function describePropertyCondition(condition: SurchargePropertyConditionDTO): string {
  const property = surchargeConditionPropertyTitle[condition.property];
  const operator = surchargeConditionOperatorTitle[condition.operator];
  const value = Array.isArray(condition.value)
    ? `[${condition.value.join(', ')}]`
    : String(condition.value);
  return `${property} ${operator} ${value}`;
}

/**
 * Flattens an applicability tree to a readable line, e.g.
 * "ALL of: Carrier code is equal to TGE; Destination country code is one of [NZ, AU]".
 * Nested groups recurse (parenthesised) so the recursive DTO renders without losing structure.
 */
export function describeApplicability(condition: SurchargeConditionDTO): string {
  const joiner = condition.childConditionType === SurchargeConditionChildType.ALL ? 'ALL' : 'ANY';
  const parts = condition.childConditions.map((child) =>
    isPropertyCondition(child)
      ? describePropertyCondition(child)
      : `(${describeApplicability(child)})`,
  );
  return `${joiner} of: ${parts.join('; ')}`;
}

/**
 * Hydrates the form's applicability section from a stored DTO (used by no caller yet, but keeps the
 * round-trip honest and is ready for an "edit conditions" follow-up). Nested groups are flattened to
 * their property rows — the single-level builder cannot represent nesting, so only direct property
 * children are surfaced.
 */
export function toConditionsForm(
  applicability: SurchargeConditionDTO | null,
): SurchargeConditionsForm {
  if (!applicability) return emptyConditionsForm();
  const rows = applicability.childConditions.filter(isPropertyCondition).map((condition) => {
    const isArray = isArrayOperator(condition.operator);
    return {
      property: condition.property,
      operator: condition.operator,
      value: isArray ? '' : String(condition.value),
      values: isArray && Array.isArray(condition.value) ? condition.value.map(String) : [],
    };
  });
  return {
    enabled: rows.length > 0,
    childConditionType: applicability.childConditionType,
    rows: rows.length > 0 ? rows : [emptyConditionRow()],
  };
}
